import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, ChevronLeft, ChevronRight, Plus, X, Clock, Trash2, Loader2, Users as UsersIcon, User, Phone, Mail, MapPin, Navigation, MessageSquare, Send, CheckCircle, Bell, Link2, ClipboardList, NotebookText } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../../context/AuthContext';
import { moduleApi, notesApi } from '../../lib/api';
import { SendCustomMessageModal } from '../ui/SendCustomMessageModal';
import { SendDocumentByEmailModal } from '../RdvPro/SendDocumentByEmailModal';
import { AdminCompletionModal } from './AdminCompletionModal';
import { LinkedNotesPanel, LinkedNotesQuickModal } from '../Notes/LinkedNotesPanel';
import {
    WeeklyHours, DAY_LABELS, defaultWeeklyHours,
    PX_PER_HOUR, timeToMinutes, minutesToTime, formatDateInput, formatDateHuman, contactLabel,
    dayKeyForDate, dayShortLabel, packAppointments, EmailLogEntry, EMAIL_TYPE_LABELS,
} from './prestationPlanningShared';

interface PrestationCatalogItem {
    id: string;
    label: string;
    unit_price_ttc: number;
    tax_rate: number;
    show_tva: boolean;
    default_duration_minutes: number;
}

interface Employee {
    id: string;
    first_name: string;
    last_name: string;
}

interface CrmSuggestion {
    id: string;
    type: 'PRIVÉ' | 'PROFESSIONNEL';
    first_name?: string;
    last_name?: string;
    company_name?: string;
    phone?: string;
    email?: string;
    address?: string;
    postal_code?: string;
    city?: string;
    country?: string;
}

interface Appointment {
    id: string;
    crm_contact_id: string | null;
    crm_first_name?: string;
    crm_last_name?: string;
    crm_company_name?: string;
    crm_phone?: string;
    crm_email?: string;
    crm_address?: string;
    crm_postal_code?: string;
    crm_city?: string;
    crm_country?: string;
    status?: string;
    location_type?: 'domicile' | 'entreprise';
    total_amount_ttc?: number;
    amount_already_paid?: number;
    km_fee_amount?: number;
    employee_ids: string[];
    prestation_catalog_item_id: string | null;
    prestation_label: string;
    quantity: number;
    unit_price_ttc: number;
    tax_rate: number;
    catalog_items?: { catalog_item_id: string | null; label: string; quantity: number; unit_price_ttc: number; tax_rate: number; unit_duration_minutes: number }[];
    unit_duration_minutes: number;
    computed_duration_minutes: number;
    appointment_date: string;
    start_time: string;
    payment_status: string;
    payment_method: string;
    payment_breakdown?: { method: string; amount: number }[];
    completed_at?: string;
    facture_id?: string;
    notes: string;
}

const PAYMENT_STATUS_LABELS: Record<string, string> = {
    a_encaisser: 'À encaisser',
    encaisse: 'Encaissé',
    a_regler_sous_x_jours: 'À régler (délai)',
};

const employeeNamesLabel = (appt: Appointment, employees: Employee[]) => {
    return (appt.employee_ids || [])
        .map((id) => {
            const e = employees.find((x) => x.id === id);
            return e ? `${e.first_name} ${e.last_name}`.trim() : '';
        })
        .filter(Boolean)
        .join(', ');
};

function AppointmentModal({
    initial, catalog, employees, date, weeklyHours, onClose, onSave, onDelete,
}: {
    initial: Appointment | null;
    catalog: PrestationCatalogItem[];
    employees: Employee[];
    date: string;
    weeklyHours: WeeklyHours;
    onClose: () => void;
    onSave: (payload: any) => Promise<void>;
    onDelete?: () => Promise<void>;
}) {
    const { user } = useAuth();
    const locationMode = (user as any)?.prestation_location_mode || 'domicile';
    const [appointmentDate, setAppointmentDate] = useState(initial?.appointment_date || date);
    const dayHoursForModal = weeklyHours[dayKeyForDate(appointmentDate)];
    const minTime = dayHoursForModal.opening;
    const maxTime = dayHoursForModal.closing;
    const [crmContactId, setCrmContactId] = useState(initial?.crm_contact_id || '');
    const [crmType, setCrmType] = useState<'PRIVÉ' | 'PROFESSIONNEL'>('PRIVÉ');
    const [firstName, setFirstName] = useState(initial?.crm_first_name || '');
    const [lastName, setLastName] = useState(initial?.crm_last_name || '');
    const [companyName, setCompanyName] = useState(initial?.crm_company_name || '');
    const [phone, setPhone] = useState(initial?.crm_phone || '');
    const [email, setEmail] = useState(initial?.crm_email || '');
    const [address, setAddress] = useState(initial?.crm_address || '');
    const [postalCode, setPostalCode] = useState(initial?.crm_postal_code || '');
    const [city, setCity] = useState(initial?.crm_city || '');
    const [lookupField, setLookupField] = useState<'firstName' | 'lastName' | 'companyName' | null>(null);
    const [lookupTerm, setLookupTerm] = useState('');
    const [suggestions, setSuggestions] = useState<CrmSuggestion[]>([]);
    const [searching, setSearching] = useState(false);

    interface AppointmentCatalogItem { catalog_item_id: string | null; label: string; quantity: number; unit_price_ttc: number; tax_rate: number; unit_duration_minutes: number; }
    const initialCatalogItems: AppointmentCatalogItem[] = Array.isArray(initial?.catalog_items) && initial.catalog_items.length > 0
        ? initial.catalog_items
        : (initial?.prestation_label ? [{
            catalog_item_id: initial?.prestation_catalog_item_id || null,
            label: initial.prestation_label,
            quantity: initial?.quantity ?? 1,
            unit_price_ttc: initial?.unit_price_ttc ?? 0,
            tax_rate: initial?.tax_rate ?? 0,
            unit_duration_minutes: initial?.unit_duration_minutes ?? 0,
        }] : []);
    const [prestationItems, setPrestationItems] = useState<AppointmentCatalogItem[]>(initialCatalogItems);
    const [employeeIds, setEmployeeIds] = useState<string[]>(initial?.employee_ids || []);
    const [startTime, setStartTime] = useState(initial?.start_time || '09:00');
    const [paymentStatus, setPaymentStatus] = useState(initial?.payment_status || 'a_encaisser');
    const [paymentMethod, setPaymentMethod] = useState(initial?.payment_method || '');
    const [notes, setNotes] = useState(initial?.notes || '');
    const [locationType, setLocationType] = useState<'domicile' | 'entreprise'>(
        initial?.location_type || (locationMode === 'entreprise' ? 'entreprise' : 'domicile')
    );
    const [recurrenceFrequency, setRecurrenceFrequency] = useState<'none' | 'weekly' | 'biweekly' | 'monthly'>('none');
    const [recurrenceOccurrences, setRecurrenceOccurrences] = useState(8);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const q = lookupTerm.trim();
        if (q.length < 2) { setSuggestions([]); setSearching(false); return; }
        let cancelled = false;
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const items = await moduleApi.searchCRMContacts(q);
                if (!cancelled) setSuggestions(Array.isArray(items) ? items : []);
            } catch {
                if (!cancelled) setSuggestions([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [lookupTerm]);

    const triggerLookup = (field: 'firstName' | 'lastName' | 'companyName', value: string) => {
        setLookupField(field);
        setLookupTerm(value);
    };

    const applyCRMContact = (s: CrmSuggestion) => {
        if (s.type === 'PRIVÉ' || s.type === 'PROFESSIONNEL') setCrmType(s.type);
        setFirstName(s.first_name || '');
        setLastName(s.last_name || '');
        setCompanyName(s.company_name || '');
        setPhone(s.phone || '');
        setEmail(s.email || '');
        setAddress(s.address || '');
        setPostalCode(s.postal_code || '');
        setCity(s.city || '');
        setCrmContactId(s.id);
        setSuggestions([]);
        setLookupTerm('');
        setLookupField(null);
    };

    const renderSuggestions = (field: 'firstName' | 'lastName' | 'companyName') => {
        if (lookupField !== field) return null;
        if (!searching && suggestions.length === 0) return null;
        return (
            <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded-xl shadow-xl max-h-48 overflow-y-auto">
                {searching && <div className="px-3 py-2 text-[11px] text-content-muted">Recherche CRM...</div>}
                {!searching && suggestions.map((s) => (
                    <button key={s.id} type="button" onMouseDown={() => applyCRMContact(s)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-surface-soft transition-colors text-content">
                        <div className="font-semibold truncate">{s.type === 'PROFESSIONNEL' ? (s.company_name || 'Entreprise') : [s.first_name, s.last_name].filter(Boolean).join(' ')}</div>
                        {s.phone && <div className="text-[11px] text-content-muted">{s.phone}</div>}
                    </button>
                ))}
            </div>
        );
    };

    const addPrestationItem = (catalogItemId: string) => {
        const found = catalog.find((i) => i.id === catalogItemId);
        if (!found) return;
        setPrestationItems((prev) => [...prev, {
            catalog_item_id: found.id,
            label: found.label,
            quantity: 1,
            unit_price_ttc: found.unit_price_ttc,
            tax_rate: found.show_tva ? found.tax_rate : 0,
            unit_duration_minutes: found.default_duration_minutes,
        }]);
    };
    const updatePrestationItem = (idx: number, patch: Partial<AppointmentCatalogItem>) =>
        setPrestationItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    const removePrestationItem = (idx: number) => setPrestationItems((prev) => prev.filter((_, i) => i !== idx));

    const toggleEmployee = (id: string) => {
        setEmployeeIds((prev) => prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]);
    };

    const totalPrestationDuration = prestationItems.reduce((sum, it) => sum + (Number(it.quantity || 0) * Number(it.unit_duration_minutes || 0)), 0);
    const computedDuration = employeeIds.length > 0 ? totalPrestationDuration / employeeIds.length : 0;

    const handleSubmit = async () => {
        setError('');
        if (prestationItems.length === 0) { setError('Sélectionnez au moins une prestation.'); return; }
        if (employeeIds.length === 0) { setError('Assignez au moins un employé.'); return; }
        if (!firstName.trim() && !lastName.trim() && !companyName.trim()) { setError('Renseignez le client (nom ou société).'); return; }
        setSaving(true);
        try {
            await onSave({
                crm_contact_id: crmContactId || undefined,
                crm_type: crmType,
                first_name: firstName,
                last_name: lastName,
                company_name: companyName,
                phone,
                email,
                address,
                postal_code: postalCode,
                city,
                employee_ids: employeeIds,
                catalog_items: prestationItems,
                appointment_date: appointmentDate,
                start_time: startTime,
                payment_status: paymentStatus,
                payment_method: paymentMethod,
                notes,
                location_type: locationType,
                ...(recurrenceFrequency !== 'none' ? { recurrence: { frequency: recurrenceFrequency, occurrences: recurrenceOccurrences } } : {}),
            });
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-xl bg-surface border border-border rounded-3xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
                    <h3 className="text-lg font-bold text-content flex items-center gap-2">
                        <CalendarClock size={18} className="text-brand-600 dark:text-brand-400" />
                        {initial?.status === 'demande' ? 'Confirmer la demande de réservation' : initial ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
                    </h3>
                    <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft"><X size={20} /></button>
                </div>
                {initial?.status === 'demande' && (
                    <div className="mx-6 mt-4 p-3 rounded-xl bg-warning-500/10 border border-warning-500/20 text-xs text-warning-500 font-semibold">
                        Demande reçue via le widget de réservation en ligne — assignez au moins un salarié puis validez pour confirmer le rendez-vous au client final.
                    </div>
                )}
                <div className="p-6 space-y-5 overflow-y-auto">
                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Client</label>
                        <div className="flex items-center gap-4">
                            <button type="button" onClick={() => setCrmType('PRIVÉ')}
                                className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition-all ${crmType === 'PRIVÉ' ? 'border-brand-600 text-content' : 'border-transparent text-content-muted hover:text-content'}`}>
                                Particulier
                            </button>
                            <button type="button" onClick={() => setCrmType('PROFESSIONNEL')}
                                className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition-all ${crmType === 'PROFESSIONNEL' ? 'border-brand-600 text-content' : 'border-transparent text-content-muted hover:text-content'}`}>
                                Professionnel
                            </button>
                        </div>
                    </div>

                    {crmType === 'PRIVÉ' ? (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1 relative">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prénom</label>
                                <input type="text" value={firstName}
                                    onChange={(e) => { setFirstName(e.target.value); setCrmContactId(''); triggerLookup('firstName', e.target.value); }}
                                    onBlur={() => setTimeout(() => setLookupField(null), 150)}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                {renderSuggestions('firstName')}
                            </div>
                            <div className="space-y-1 relative">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nom</label>
                                <input type="text" value={lastName}
                                    onChange={(e) => { setLastName(e.target.value); setCrmContactId(''); triggerLookup('lastName', e.target.value); }}
                                    onBlur={() => setTimeout(() => setLookupField(null), 150)}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                {renderSuggestions('lastName')}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-1 relative">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Société</label>
                            <input type="text" value={companyName}
                                onChange={(e) => { setCompanyName(e.target.value); setCrmContactId(''); triggerLookup('companyName', e.target.value); }}
                                onBlur={() => setTimeout(() => setLookupField(null), 150)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                            {renderSuggestions('companyName')}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Téléphone</label>
                            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Email</label>
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="Nécessaire pour les rappels par email"
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        </div>
                    </div>

                    {locationMode === 'les_deux' && (
                        <div className="space-y-2">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Lieu du rendez-vous</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button type="button" onClick={() => setLocationType('domicile')}
                                    className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${locationType === 'domicile' ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                    Chez le client
                                </button>
                                <button type="button" onClick={() => setLocationType('entreprise')}
                                    className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${locationType === 'entreprise' ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                    Dans l'entreprise
                                </button>
                            </div>
                        </div>
                    )}

                    {locationType === 'domicile' && (
                        <>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Adresse du rendez-vous</label>
                                <input type="text" value={address} onChange={(e) => setAddress(e.target.value)}
                                    placeholder="N° et rue"
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Code postal</label>
                                    <input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Ville</label>
                                    <input type="text" value={city} onChange={(e) => setCity(e.target.value)}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prestations</label>
                        <select value="" onChange={(e) => { if (e.target.value) addPrestationItem(e.target.value); e.target.value = ''; }}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                            <option value="">+ Ajouter une prestation...</option>
                            {catalog.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                        </select>
                        {prestationItems.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-surface-soft border border-border rounded-xl p-2">
                                <span className="flex-1 min-w-0 text-sm text-content truncate">{item.label}</span>
                                <input type="number" min={0} step="1" value={item.quantity}
                                    onChange={(e) => updatePrestationItem(idx, { quantity: Number(e.target.value || 0) })}
                                    title="Quantité" className="w-14 bg-surface border border-border rounded-lg px-2 py-1 text-sm text-content outline-none text-right" />
                                <button type="button" onClick={() => removePrestationItem(idx)} className="text-red-400 p-1 shrink-0"><X size={14} /></button>
                            </div>
                        ))}
                        {prestationItems.length === 0 && <p className="text-xs text-content-muted italic">Aucune prestation sélectionnée.</p>}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date</label>
                            <input type="date" value={appointmentDate} onChange={(e) => setAppointmentDate(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Heure de début</label>
                            <input type="time" value={startTime} min={minTime} max={maxTime} onChange={(e) => setStartTime(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Statut paiement</label>
                            <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                                {Object.entries(PAYMENT_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                    </div>
                    {dayHoursForModal.closed && (
                        <p className="text-xs text-warning-400 font-semibold">⚠️ Le {DAY_LABELS[dayKeyForDate(appointmentDate)].toLowerCase()} est marqué comme fermé dans les horaires — vérifiez avant de confirmer.</p>
                    )}

                    <div className="space-y-2">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><UsersIcon size={12} /> Employés assignés</label>
                        <div className="flex flex-wrap gap-2">
                            {employees.map((emp) => {
                                const active = employeeIds.includes(emp.id);
                                return (
                                    <button key={emp.id} type="button" onClick={() => toggleEmployee(emp.id)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${active ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                        {emp.first_name} {emp.last_name}
                                    </button>
                                );
                            })}
                            {employees.length === 0 && <p className="text-xs text-content-muted italic">Aucun employé configuré (Postes &amp; Employés).</p>}
                        </div>
                    </div>

                    {employeeIds.length > 0 && totalPrestationDuration > 0 && (
                        <div className="flex items-center gap-2 p-3 rounded-xl bg-brand-500/10 border border-brand-500/20 text-xs text-content-muted">
                            <Clock size={14} className="text-brand-600 dark:text-brand-400 shrink-0" />
                            Durée calculée : <strong className="text-content">{Math.round(computedDuration)} min</strong> ({Math.round(totalPrestationDuration)} min ÷ {employeeIds.length} employé{employeeIds.length > 1 ? 's' : ''})
                        </div>
                    )}

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Notes</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                            className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                    </div>

                    {!initial && (
                        <div className="space-y-2 p-3 rounded-xl bg-surface-soft border border-border">
                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Récurrence (contrat régulier)</label>
                            <div className="grid grid-cols-4 gap-2">
                                {([
                                    { key: 'none', label: 'Aucune' },
                                    { key: 'weekly', label: 'Chaque semaine' },
                                    { key: 'biweekly', label: 'Toutes les 2 sem.' },
                                    { key: 'monthly', label: 'Chaque mois' },
                                ] as const).map((opt) => (
                                    <button key={opt.key} type="button" onClick={() => setRecurrenceFrequency(opt.key)}
                                        className={`px-2 py-2 rounded-lg text-[11px] font-bold border transition-all ${recurrenceFrequency === opt.key ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            {recurrenceFrequency !== 'none' && (
                                <div className="flex items-center gap-2 pt-1">
                                    <label className="text-xs text-content-muted">Nombre d'occurrences à créer :</label>
                                    <input type="number" min={2} max={52} value={recurrenceOccurrences}
                                        onChange={(e) => setRecurrenceOccurrences(Math.min(52, Math.max(2, Number(e.target.value || 2))))}
                                        className="w-20 bg-surface border border-border rounded-lg px-2 py-1 text-sm text-content outline-none text-right" />
                                    <p className="text-[11px] text-content-muted italic">Une occurrence déjà en conflit avec un salarié assigné sera simplement ignorée.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {initial?.id && <LinkedNotesPanel kind="appointment" id={initial.id} />}

                    {error && <p className="text-xs text-red-400 font-semibold">{error}</p>}
                </div>
                <div className="flex justify-between items-center gap-3 p-6 border-t border-border shrink-0">
                    {initial && onDelete ? (
                        <button onClick={onDelete} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 text-sm font-bold transition-all">
                            <Trash2 size={14} /> Supprimer
                        </button>
                    ) : <span />}
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-border text-content-muted hover:text-content font-bold text-sm">Annuler</button>
                        <button onClick={handleSubmit} disabled={saving}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm disabled:opacity-50">
                            {saving ? 'Enregistrement...' : initial?.status === 'demande' ? 'Valider la demande' : initial ? 'Enregistrer' : 'Créer le rendez-vous'}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

function AppointmentDetailModal({
    appt, employees, catalog, onClose, onEdit, onDelete, onRefresh,
}: {
    appt: Appointment;
    employees: Employee[];
    catalog: PrestationCatalogItem[];
    onClose: () => void;
    onEdit: () => void;
    onDelete: () => Promise<void>;
    onRefresh: () => Promise<void>;
}) {
    const [emails, setEmails] = useState<EmailLogEntry[]>([]);
    const [loadingEmails, setLoadingEmails] = useState(true);
    const [previewEmail, setPreviewEmail] = useState<EmailLogEntry | null>(null);
    const [resendingEmailId, setResendingEmailId] = useState<string | null>(null);
    const [resendEmailResult, setResendEmailResult] = useState<{ id: string; ok: boolean; msg: string } | null>(null);
    const [sendingReminder, setSendingReminder] = useState(false);
    const [reminderMessage, setReminderMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [showMarkPaid, setShowMarkPaid] = useState(false);
    const [showSendMessage, setShowSendMessage] = useState(false);
    const [showCompletion, setShowCompletion] = useState(false);
    const [markPaidEntries, setMarkPaidEntries] = useState<{ method: string; amount: number }[]>([{ method: '', amount: 0 }]);
    const [markingPaid, setMarkingPaid] = useState(false);
    const [markPaidError, setMarkPaidError] = useState('');
    const [showAssign, setShowAssign] = useState(false);
    const [assigningIds, setAssigningIds] = useState<string[]>([]);
    const [assigning, setAssigning] = useState(false);
    const [assignError, setAssignError] = useState('');

    const toggleAssignId = (id: string) => setAssigningIds((prev) => prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]);

    // Assignation rapide, sans passer par "Modifier" (qui rouvre tout le formulaire) — renvoie le même
    // payload complet attendu par PUT /prestation-planning/appointments/:id (reconstruit à partir de ce
    // rendez-vous), avec les employés sélectionnés ici comme seule différence.
    const handleQuickAssign = async () => {
        if (assigningIds.length === 0) { setAssignError('Sélectionnez au moins un employé.'); return; }
        setAssigning(true);
        setAssignError('');
        try {
            const res: any = await moduleApi.updatePrestationAppointment(appt.id, {
                crm_contact_id: appt.crm_contact_id || undefined,
                crm_type: appt.crm_company_name ? 'PROFESSIONNEL' : 'PRIVÉ',
                first_name: appt.crm_first_name || '',
                last_name: appt.crm_last_name || '',
                company_name: appt.crm_company_name || '',
                phone: appt.crm_phone || '',
                email: appt.crm_email || '',
                address: appt.crm_address || '',
                postal_code: appt.crm_postal_code || '',
                city: appt.crm_city || '',
                employee_ids: assigningIds,
                catalog_items: appt.catalog_items && appt.catalog_items.length > 0 ? appt.catalog_items : [{
                    catalog_item_id: appt.prestation_catalog_item_id, label: appt.prestation_label, quantity: appt.quantity,
                    unit_price_ttc: appt.unit_price_ttc, tax_rate: appt.tax_rate, unit_duration_minutes: appt.unit_duration_minutes,
                }],
                appointment_date: appt.appointment_date,
                start_time: appt.start_time,
                payment_status: appt.payment_status,
                payment_method: appt.payment_method,
                notes: appt.notes,
                location_type: appt.location_type,
            });
            if (res?.error) { setAssignError(res.error); return; }
            setShowAssign(false);
            setAssigningIds([]);
            await onRefresh();
            onClose();
        } catch (e) {
            setAssignError('Erreur lors de l\'assignation.');
        } finally {
            setAssigning(false);
        }
    };

    const loadEmails = () => {
        moduleApi.getPrestationAppointmentEmails(appt.id)
            .then((d: any) => setEmails(Array.isArray(d) ? d : []))
            .catch(() => setEmails([]))
            .finally(() => setLoadingEmails(false));
    };

    useEffect(() => { loadEmails(); }, [appt.id]);

    // Renvoie exactement l'email choisi dans l'historique (même sujet, même contenu déjà envoyé) — pas besoin
    // de savoir quel type c'est ni de recalculer son contenu, contrairement aux boutons "Renvoyer" dédiés
    // (confirmation/rappel/facture) qui existent par ailleurs.
    const handleResendEmail = async (emailId: string) => {
        setResendingEmailId(emailId);
        setResendEmailResult(null);
        try {
            const res: any = await moduleApi.resendPrestationAppointmentEmail(appt.id, emailId);
            if (res?.success) {
                setResendEmailResult({ id: emailId, ok: true, msg: 'Envoyé.' });
                loadEmails();
            } else {
                setResendEmailResult({ id: emailId, ok: false, msg: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setResendEmailResult({ id: emailId, ok: false, msg: 'Erreur lors de l\'envoi.' });
        } finally {
            setResendingEmailId(null);
        }
    };

    const handleSendReminder = async () => {
        setSendingReminder(true);
        setReminderMessage(null);
        try {
            const res: any = await moduleApi.sendPrestationReminder(appt.id);
            if (res?.success) {
                setReminderMessage({ type: 'success', text: res?.warning || 'Rappel envoyé.' });
                loadEmails();
            } else {
                setReminderMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setReminderMessage({ type: 'error', text: 'Erreur lors de l\'envoi.' });
        } finally {
            setSendingReminder(false);
        }
    };

    // "Relancer pour le paiement" — relance le client sur le solde restant d'une prestation déjà clôturée
    // (réutilise le même email facture, dont le corps s'adapte déjà au statut de paiement — reste à
    // payer/échéance — plutôt que d'inventer un nouveau type de message).
    const handleSendPaymentReminder = async () => {
        if (!appt.facture_id || !appt.crm_email) {
            setReminderMessage({ type: 'error', text: appt.crm_email ? 'Facture introuvable pour ce rendez-vous.' : 'Aucune adresse email renseignée pour ce client.' });
            return;
        }
        setSendingReminder(true);
        setReminderMessage(null);
        try {
            const factures: any[] = await moduleApi.getFactures();
            const invoice = (Array.isArray(factures) ? factures : []).find((f: any) => String(f.id) === String(appt.facture_id));
            if (!invoice) { setReminderMessage({ type: 'error', text: 'Facture introuvable.' }); return; }
            const payload = invoice.payload_json || {};
            let snapshot = invoice.billing_snapshot || {};
            if (!snapshot?.company_name) {
                try {
                    const fresh = await moduleApi.getBillingSettings();
                    if (fresh?.company_name) snapshot = { ...fresh, ...snapshot };
                } catch { /* ignore */ }
            }
            const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
            const { pdf } = await generateInvoicePdfFromData({ invoice, payload, snapshot });
            const dataUri: string = pdf.output('datauristring');
            const pdfBase64 = dataUri.split(',')[1];
            const res: any = await moduleApi.sendPrestationInvoiceEmail(appt.id, { to: appt.crm_email, pdfBase64, filename: `${invoice.invoice_number || 'facture'}.pdf` });
            if (res?.success) {
                setReminderMessage({ type: 'success', text: 'Relance envoyée.' });
                loadEmails();
            } else {
                setReminderMessage({ type: 'error', text: res?.error || 'Erreur lors de l\'envoi.' });
            }
        } catch {
            setReminderMessage({ type: 'error', text: 'Erreur lors de l\'envoi.' });
        } finally {
            setSendingReminder(false);
        }
    };

    const endTime = minutesToTime(timeToMinutes(appt.start_time) + Math.round(appt.computed_duration_minutes));
    const fullAddress = [appt.crm_address, [appt.crm_postal_code, appt.crm_city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const mapsUrl = fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : null;
    const remainingDue = Math.max(0, Number(appt.total_amount_ttc || 0) - Number(appt.amount_already_paid || 0));
    const isPendingRequest = appt.status === 'demande';
    const isClosed = appt.status === 'termine';
    // Une fois clôturée ET payée intégralement, plus rien à notifier ni à modifier — la "date passée"
    // n'est plus le critère : une prestation clôturée mais pas encore payée en intégralité doit au
    // contraire pouvoir être relancée pour le paiement, peu importe la date.
    const isFullyPaid = isClosed && Number(appt.total_amount_ttc || 0) > 0 && remainingDue <= 0;
    const isClosedUnpaid = isClosed && remainingDue > 0;
    const fmt = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

    const markPaidEntriesSum = markPaidEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
    const markPaidEntriesValid = markPaidEntries.every((e) => e.method && Number(e.amount) > 0) && Math.abs(markPaidEntriesSum - remainingDue) < 0.01;
    const updateMarkPaidEntry = (idx: number, patch: Partial<{ method: string; amount: number }>) =>
        setMarkPaidEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
    const addMarkPaidEntry = () => {
        const allocated = markPaidEntries.reduce((s, e) => s + Number(e.amount || 0), 0);
        setMarkPaidEntries((prev) => [...prev, { method: '', amount: Math.max(0, Math.round((remainingDue - allocated) * 100) / 100) }]);
    };
    const removeMarkPaidEntry = (idx: number) => setMarkPaidEntries((prev) => prev.filter((_, i) => i !== idx));

    const handleMarkPaid = async () => {
        if (!markPaidEntriesValid) return;
        setMarkingPaid(true);
        setMarkPaidError('');
        try {
            const res: any = await moduleApi.markPrestationAppointmentPaid(appt.id, markPaidEntries);
            if (res?.error) { setMarkPaidError(res.error); setMarkingPaid(false); return; }
            // L'email "prestation soldée" part quoi qu'il arrive — la facture PDF n'est jointe que si le
            // module Facture est actif (res.invoice alors renseigné).
            if (res?.recipient_email) {
                try {
                    let pdfBase64: string | undefined, filename: string | undefined;
                    if (res.invoice) {
                        const { generateInvoicePdfFromData } = await import('../Factures/invoicePdfGenerator');
                        const { pdf } = await generateInvoicePdfFromData({ invoice: res.invoice, payload: res.payload, snapshot: res.snapshot });
                        const dataUri: string = pdf.output('datauristring');
                        pdfBase64 = dataUri.split(',')[1];
                        filename = `${res.invoice?.invoice_number || 'facture'}.pdf`;
                    }
                    await moduleApi.sendPrestationInvoiceEmail(appt.id, { to: res.recipient_email, pdfBase64, filename });
                } catch (pdfErr) {
                    console.error('Failed to generate/send invoice email', pdfErr);
                }
            }
            setShowMarkPaid(false);
            await onRefresh();
            onClose();
        } catch (e) {
            setMarkPaidError('Erreur lors de la mise à jour du paiement.');
        } finally {
            setMarkingPaid(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-lg bg-surface border border-border rounded-3xl max-h-[90vh] shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-11 h-11 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                            <User size={20} />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-lg font-bold text-content truncate">{contactLabel(appt)}</h3>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {isPendingRequest ? (
                            <span className="text-[10px] font-black uppercase bg-warning-500/10 text-warning-500 border border-warning-500/20 px-2 py-1 rounded-lg">Demande à confirmer</span>
                        ) : appt.status === 'termine' ? (
                            <span className="text-[10px] font-black uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-1 rounded-lg">Terminé</span>
                        ) : (
                            <span className="text-[10px] font-black uppercase bg-warning-500/10 text-warning-400 border border-warning-500/20 px-2 py-1 rounded-lg">Planifié</span>
                        )}
                        <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft"><X size={20} /></button>
                    </div>
                </div>
                {isPendingRequest && (
                    <div className="mx-6 mt-4 p-3 rounded-xl bg-warning-500/10 border border-warning-500/20 text-xs text-warning-500 font-semibold">
                        Reçue via le widget de réservation en ligne, en attente d'assignation d'un salarié. Cliquez sur "Modifier" pour assigner et confirmer.
                    </div>
                )}
                <div className="p-6 space-y-5 overflow-y-auto">
                    <div className="p-4 rounded-2xl bg-surface-soft border border-border">
                        <div className="flex items-center justify-between text-content">
                            <span className="text-xs text-content-muted">Début</span>
                            <span className="font-bold text-sm">{appt.start_time}</span>
                        </div>
                        <div className="flex items-center justify-between text-content mt-2">
                            <span className="text-xs text-content-muted">Fin (approximative)</span>
                            <span className="font-bold text-sm">{endTime}</span>
                        </div>
                        <div className="mt-3 pt-3 border-t border-border text-[10px] text-content-muted font-bold uppercase tracking-wider capitalize">
                            {formatDateHuman(appt.appointment_date)}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prestations</h4>
                        <div className="space-y-1">
                            {(appt.catalog_items && appt.catalog_items.length > 0 ? appt.catalog_items : [{ label: appt.prestation_label, quantity: appt.quantity, unit_price_ttc: appt.unit_price_ttc, catalog_item_id: null, tax_rate: 0, unit_duration_minutes: 0 }]).map((item, idx) => (
                                <p key={idx} className="text-sm text-content">
                                    {item.label}{item.quantity > 1 ? ` × ${item.quantity}` : ''}
                                </p>
                            ))}
                            {!!appt.km_fee_amount && appt.km_fee_amount > 0 && (
                                <p className="text-sm text-content flex items-center justify-between">
                                    <span>Frais kilométriques (réglés en ligne)</span>
                                    <span className="font-bold">{fmt(appt.km_fee_amount)}</span>
                                </p>
                            )}
                        </div>
                    </div>

                    {appt.status === 'termine' && (
                        <div className="space-y-2">
                            <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Paiement</h4>
                            <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-1.5 text-sm">
                                {(appt.payment_breakdown && appt.payment_breakdown.length > 0
                                    ? appt.payment_breakdown
                                    : (appt.payment_method ? [{ method: appt.payment_method, amount: Number(appt.amount_already_paid || 0) }] : [])
                                ).map((entry, idx) => (
                                    <div key={idx} className="flex items-center justify-between">
                                        <span className="text-content-muted">{entry.method}</span>
                                        <span className="font-bold text-content">{fmt(entry.amount)}</span>
                                    </div>
                                ))}
                                {!appt.payment_method && (!appt.payment_breakdown || appt.payment_breakdown.length === 0) && (
                                    <p className="text-content-muted italic text-xs">Aucun règlement enregistré.</p>
                                )}
                                <div className="pt-1.5 border-t border-border flex items-center justify-between">
                                    <span className="text-content-muted">{isFullyPaid ? 'Total réglé' : 'Réglé à ce jour'}</span>
                                    <span className={`font-black ${isFullyPaid ? 'text-emerald-400' : 'text-content'}`}>{fmt(Number(appt.amount_already_paid || 0))}</span>
                                </div>
                                {appt.completed_at && (
                                    <p className="text-[11px] text-content-muted pt-0.5">
                                        Clôturé le {new Date(appt.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {appt.status === 'termine' && appt.payment_status !== 'encaisse' && remainingDue > 0 && (
                        <div className="p-4 rounded-2xl bg-red-500/5 border border-red-500/20 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-content-muted uppercase tracking-widest">Reste à payer</span>
                                <span className="font-black text-red-400">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(remainingDue)}</span>
                            </div>
                            {!showMarkPaid ? (
                                <button onClick={() => { setMarkPaidEntries([{ method: '', amount: remainingDue }]); setShowMarkPaid(true); }} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all">
                                    <CheckCircle size={14} /> Marquer comme payé
                                </button>
                            ) : (
                                <div className="space-y-2">
                                    <p className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Moyen(s) de paiement utilisé(s)</p>
                                    {markPaidEntries.map((entry, idx) => (
                                        <div key={idx} className="space-y-1.5 p-2 rounded-xl bg-surface border border-border">
                                            <div className="flex gap-2">
                                                {['Espèces', 'Virement', 'Carte bleue'].map((m) => (
                                                    <button key={m} onClick={() => updateMarkPaidEntry(idx, { method: m })}
                                                        className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-bold border transition-all ${entry.method === m ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-border text-content-muted hover:text-content'}`}>
                                                        {m}
                                                    </button>
                                                ))}
                                            </div>
                                            {markPaidEntries.length > 1 && (
                                                <div className="flex items-center gap-2">
                                                    <input type="number" min={0} step="0.01" value={entry.amount}
                                                        onChange={(e) => updateMarkPaidEntry(idx, { amount: Number(e.target.value || 0) })}
                                                        placeholder="Montant" title="Montant pour ce moyen de paiement"
                                                        className="flex-1 bg-surface-soft border border-border rounded-lg px-2 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                                                    <button type="button" onClick={() => removeMarkPaidEntry(idx)} className="text-red-400 p-1 shrink-0"><Trash2 size={14} /></button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    <button type="button" onClick={addMarkPaidEntry} className="text-xs font-bold text-brand-600 dark:text-brand-400 flex items-center gap-1.5">
                                        <Plus size={14} /> Ajouter un autre moyen de paiement
                                    </button>
                                    {markPaidEntries.length > 1 && Math.abs(markPaidEntriesSum - remainingDue) >= 0.01 && (
                                        <p className="text-xs text-red-400 font-semibold">
                                            La somme des moyens de paiement ({new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(markPaidEntriesSum)}) ne correspond pas au reste à payer ({new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(remainingDue)}).
                                        </p>
                                    )}
                                    {markPaidError && <p className="text-xs text-red-400 font-semibold">{markPaidError}</p>}
                                    <div className="flex gap-2">
                                        <button onClick={() => setShowMarkPaid(false)} className="flex-1 px-3 py-2 rounded-lg border border-border text-content-muted hover:text-content text-xs font-bold">Annuler</button>
                                        <button onClick={handleMarkPaid} disabled={!markPaidEntriesValid || markingPaid} className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50">
                                            {markingPaid ? 'Validation...' : 'Confirmer'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Coordonnées client</h4>
                        <div className="space-y-1.5 text-sm text-content">
                            {appt.crm_phone && <p className="flex items-center gap-2"><Phone size={13} className="text-content-muted shrink-0" /> {appt.crm_phone}</p>}
                            {appt.crm_email && <p className="flex items-center gap-2"><Mail size={13} className="text-content-muted shrink-0" /> {appt.crm_email}</p>}
                            {!appt.crm_phone && !appt.crm_email && <p className="text-content-muted italic text-xs">Aucune coordonnée renseignée</p>}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><MapPin size={12} /> Lieu du rendez-vous</h4>
                        <span className="inline-block text-[10px] font-black uppercase px-2 py-0.5 rounded-lg bg-surface-soft border border-border text-content-muted">
                            {appt.location_type === 'entreprise' ? "Dans l'entreprise" : 'Chez le client'}
                        </span>
                        {fullAddress ? (
                            <>
                                <p className="text-sm text-content">{fullAddress}</p>
                                {mapsUrl && (
                                    <a href={mapsUrl} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                        <Navigation size={13} /> Ouvrir sur Maps
                                    </a>
                                )}
                            </>
                        ) : <p className="text-content-muted italic text-xs">Non renseignée</p>}
                    </div>

                    <LinkedNotesPanel kind="appointment" id={appt.id} />

                    <div className="space-y-2">
                        <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><UsersIcon size={12} /> Employé(s) assigné(s)</h4>
                        {employeeNamesLabel(appt, employees) ? (
                            <p className="text-sm text-content">{employeeNamesLabel(appt, employees)}</p>
                        ) : (
                            <>
                                <p className="text-content-muted italic text-xs">Aucun</p>
                                {!showAssign ? (
                                    <button onClick={() => { setAssigningIds([]); setAssignError(''); setShowAssign(true); }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-[11px] font-bold transition-all">
                                        <UsersIcon size={12} /> Assigner un salarié
                                    </button>
                                ) : (
                                    <div className="p-3 rounded-xl bg-surface-soft border border-border space-y-2">
                                        {employees.length === 0 ? (
                                            <p className="text-xs text-content-muted italic">Aucun employé.</p>
                                        ) : (
                                            <div className="flex flex-wrap gap-1.5">
                                                {employees.map((e) => (
                                                    <button key={e.id} type="button" onClick={() => toggleAssignId(e.id)}
                                                        className={`px-2.5 py-1.5 rounded-lg text-[11px] font-bold border transition-all ${assigningIds.includes(e.id) ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                        {e.first_name} {e.last_name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        {assignError && <p className="text-xs text-red-400 font-semibold">{assignError}</p>}
                                        <div className="flex gap-2">
                                            <button onClick={() => setShowAssign(false)} className="flex-1 px-3 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-xs font-bold">Annuler</button>
                                            <button onClick={handleQuickAssign} disabled={assigning || assigningIds.length === 0}
                                                className="flex-1 px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold disabled:opacity-50">
                                                {assigning ? 'Assignation...' : 'Confirmer'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {appt.notes && (
                        <div className="space-y-2">
                            <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Notes</h4>
                            <p className="text-sm text-content-muted whitespace-pre-wrap">{appt.notes}</p>
                        </div>
                    )}

                    {!isFullyPaid && !isPendingRequest && (
                        <div className="space-y-2">
                            <button onClick={isClosedUnpaid ? handleSendPaymentReminder : handleSendReminder} disabled={sendingReminder}
                                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all ${isClosedUnpaid ? 'border-warning-500/30 bg-warning-500/10 text-warning-400 hover:bg-warning-500/20' : 'border-border text-content-muted hover:text-content hover:bg-surface-soft'}`}>
                                <Bell size={13} /> {sendingReminder ? 'Envoi...' : (isClosedUnpaid ? 'Relancer pour le paiement' : 'Envoyer un rappel de rendez-vous')}
                            </button>
                            {reminderMessage && (
                                <p className={`text-xs font-semibold ${reminderMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>{reminderMessage.text}</p>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <h4 className="text-[10px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><MessageSquare size={12} /> Historique message (client)</h4>
                            {appt.crm_email && (
                                <button onClick={() => setShowSendMessage(true)}
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-surface-soft border border-border text-content-muted hover:text-content text-[11px] font-bold transition-all">
                                    <Mail size={11} /> Message personnalisé
                                </button>
                            )}
                        </div>
                        {loadingEmails ? (
                            <div className="flex py-2"><Loader2 className="animate-spin text-content-muted" size={16} /></div>
                        ) : emails.length === 0 ? (
                            <p className="text-content-muted italic text-xs">Aucun email envoyé pour ce rendez-vous pour l'instant.</p>
                        ) : (
                            <div className="space-y-2">
                                {emails.map((e) => (
                                    <div key={e.id} className="p-2.5 rounded-xl bg-surface-soft border border-border">
                                        <button onClick={() => setPreviewEmail(e)} className="w-full text-left">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-content">{EMAIL_TYPE_LABELS[e.email_type] || e.subject}</span>
                                                <span className="text-[10px] text-content-muted shrink-0 ml-2">{new Date(e.sent_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                            <p className="text-[11px] text-content-muted mt-0.5">Envoyé à {e.recipient_email}</p>
                                        </button>
                                        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-border">
                                            <button onClick={() => handleResendEmail(e.id)} disabled={resendingEmailId === e.id}
                                                className="flex items-center gap-1 text-[11px] font-bold text-content-muted hover:text-content disabled:opacity-50 transition-all">
                                                <Send size={10} /> {resendingEmailId === e.id ? 'Envoi...' : 'Renvoyer'}
                                            </button>
                                            {resendEmailResult?.id === e.id && (
                                                <span className={`text-[11px] font-semibold ${resendEmailResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>{resendEmailResult.msg}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex justify-between items-center gap-3 p-6 border-t border-border shrink-0">
                    <button onClick={onDelete} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 text-sm font-bold transition-all">
                        <Trash2 size={14} /> Supprimer
                    </button>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-border text-content-muted hover:text-content font-bold text-sm">Fermer</button>
                        {!isFullyPaid && (
                            <button onClick={onEdit} className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm">Modifier</button>
                        )}
                        {appt.status !== 'termine' && (
                            <button onClick={() => setShowCompletion(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm transition-all">
                                <CheckCircle size={16} /> Terminer la prestation
                            </button>
                        )}
                    </div>
                </div>
            </motion.div>
            {showCompletion && (
                <AdminCompletionModal
                    appt={appt}
                    catalog={catalog}
                    onClose={() => setShowCompletion(false)}
                    onDone={async () => { setShowCompletion(false); onClose(); await onRefresh(); }}
                />
            )}
            {showSendMessage && (
                <SendCustomMessageModal
                    recipientLabel={contactLabel(appt)}
                    tokens={[
                        { token: '{{prenom}}', desc: 'Prénom du client' },
                        { token: '{{nom}}', desc: 'Nom (ou raison sociale) du client' },
                        { token: '{{date}}', desc: 'Date du rendez-vous' },
                        { token: '{{heure}}', desc: 'Heure du rendez-vous' },
                        { token: '{{entreprise}}', desc: 'Votre établissement' },
                        { token: '{{logo}}', desc: 'Votre logo' },
                    ]}
                    onClose={() => setShowSendMessage(false)}
                    onSend={async (subject, body) => {
                        const res = await moduleApi.sendPrestationCustomMessage(appt.id, { subject, body });
                        if (res?.success) loadEmails();
                        return res;
                    }}
                />
            )}
            {previewEmail && (
                <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
                    <div className="w-full max-w-lg bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
                            <div className="min-w-0">
                                <h3 className="font-bold text-content text-sm truncate">{previewEmail.subject || EMAIL_TYPE_LABELS[previewEmail.email_type] || 'Email'}</h3>
                                <p className="text-xs text-content-muted mt-0.5 truncate">Envoyé à {previewEmail.recipient_email} le {new Date(previewEmail.sent_at).toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                            <button onClick={() => setPreviewEmail(null)} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft shrink-0"><X size={18} /></button>
                        </div>
                        <div className="p-5 overflow-y-auto bg-surface-soft">
                            {previewEmail.html_body ? (
                                <div className="bg-white rounded-xl overflow-hidden border border-border">
                                    <iframe srcDoc={previewEmail.html_body} className="w-full" style={{ height: '55vh', border: 'none' }} sandbox="" title="Aperçu de l'email" />
                                </div>
                            ) : (
                                <p className="text-xs text-content-muted italic">Le contenu de cet email n'a pas été conservé (envoyé avant l'ajout de cet aperçu) — impossible de l'afficher ou de le renvoyer tel quel.</p>
                            )}
                        </div>
                        <div className="flex justify-between items-center gap-3 p-5 border-t border-border shrink-0">
                            {resendEmailResult?.id === previewEmail.id && (
                                <span className={`text-xs font-semibold ${resendEmailResult.ok ? 'text-emerald-400' : 'text-red-400'}`}>{resendEmailResult.msg}</span>
                            )}
                            <div className="flex gap-3 ml-auto">
                                <button onClick={() => setPreviewEmail(null)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Fermer</button>
                                {previewEmail.html_body && (
                                    <button onClick={() => handleResendEmail(previewEmail.id)} disabled={resendingEmailId === previewEmail.id}
                                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                                        <Send size={14} /> {resendingEmailId === previewEmail.id ? 'Envoi...' : 'Renvoyer cet email'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export const PrestationPlanningModule: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [showSendDocumentModal, setShowSendDocumentModal] = useState(false);
    const [showSendLinkModal, setShowSendLinkModal] = useState(false);
    const [sendLinkEmail, setSendLinkEmail] = useState('');
    const [sendingLink, setSendingLink] = useState(false);
    const [sendLinkMessage, setSendLinkMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const handleSendBookingLink = async () => {
        if (!sendLinkEmail.trim()) return;
        setSendingLink(true);
        setSendLinkMessage(null);
        try {
            await moduleApi.sendBookingLinkEmail({ email: sendLinkEmail.trim() });
            setSendLinkMessage({ type: 'success', text: 'Email envoyé avec succès.' });
            setSendLinkEmail('');
        } catch (e: any) {
            setSendLinkMessage({ type: 'error', text: e?.message || "Erreur lors de l'envoi." });
        } finally {
            setSendingLink(false);
        }
    };
    const [pendingRequestCount, setPendingRequestCount] = useState(0);
    useEffect(() => {
        if (user?.type !== 'client') return;
        const loadPendingCount = () => {
            moduleApi.getPrestationPendingRequestsCount().then((d: any) => setPendingRequestCount(d?.count || 0)).catch(() => {});
        };
        loadPendingCount();
        window.addEventListener('prestationBookingRequestProcessed', loadPendingCount);
        return () => window.removeEventListener('prestationBookingRequestProcessed', loadPendingCount);
    }, [user?.type]);

    // Cloche de notification du Planning : clique -> liste des demandes en attente -> clique sur une
    // demande -> ouvre sa fiche pour assigner un salarié et l'accepter, sans avoir à la chercher dans le
    // calendrier semaine par semaine (même donnée que "Demandes récurrentes" côté rendez-vous en attente,
    // accessible directement depuis l'Agenda).
    const [showPendingBell, setShowPendingBell] = useState(false);
    const [pendingRequestsList, setPendingRequestsList] = useState<Appointment[]>([]);
    const [loadingPendingList, setLoadingPendingList] = useState(false);
    const toggleBell = () => {
        const next = !showPendingBell;
        setShowPendingBell(next);
        if (next) {
            setLoadingPendingList(true);
            moduleApi.getPrestationPendingRequests().then((rows: any) => setPendingRequestsList(Array.isArray(rows) ? rows : []))
                .catch(() => setPendingRequestsList([]))
                .finally(() => setLoadingPendingList(false));
        }
    };
    const openPendingFromBell = (appt: Appointment) => {
        setShowPendingBell(false);
        setDate(appt.appointment_date);
        setViewMode('day');
        setDetailAppointment(appt);
    };

    // Bouton "RDV à venir" : liste (modale) des rendez-vous confirmés à venir, pour un aperçu rapide sans
    // naviguer semaine par semaine dans le calendrier.
    const [showUpcomingModal, setShowUpcomingModal] = useState(false);
    const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
    const [loadingUpcoming, setLoadingUpcoming] = useState(false);
    const openUpcomingModal = () => {
        setShowUpcomingModal(true);
        setLoadingUpcoming(true);
        moduleApi.getPrestationUpcomingAppointments().then((rows: any) => setUpcomingAppointments(Array.isArray(rows) ? rows : []))
            .catch(() => setUpcomingAppointments([]))
            .finally(() => setLoadingUpcoming(false));
    };
    const openUpcomingAppointment = (appt: Appointment) => {
        setShowUpcomingModal(false);
        setDate(appt.appointment_date);
        setViewMode('day');
        setDetailAppointment(appt);
    };
    // Deep-link depuis la cloche de la sidebar (?focus_date=...&focus_id=...) : ouvre directement le
    // Planning sur le jour de la demande en attente la plus proche, plutôt que de laisser l'établissement
    // la chercher manuellement semaine par semaine.
    const focusParams = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        const focusDate = params.get('focus_date');
        const focusId = params.get('focus_id');
        return focusDate && focusId ? { date: focusDate, id: focusId } : null;
    }, []);
    const [pendingFocusId, setPendingFocusId] = useState<string | null>(focusParams?.id || null);
    const [date, setDate] = useState(focusParams?.date || formatDateInput(new Date()));
    const [viewMode, setViewMode] = useState<'week' | 'day'>(focusParams ? 'day' : 'week');
    const [weeklyHours, setWeeklyHours] = useState<WeeklyHours>(defaultWeeklyHours());
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [catalog, setCatalog] = useState<PrestationCatalogItem[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [weekAppointments, setWeekAppointments] = useState<Record<string, Appointment[]>>({});
    const [loading, setLoading] = useState(true);
    const [modalState, setModalState] = useState<{ open: boolean; appointment: Appointment | null; date: string }>({ open: false, appointment: null, date: date });
    const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(null);
    // Indicateur "note attachée" sur la bulle du planning (LinkedNotesPanel affiche le détail à
    // l'ouverture ; ici on veut juste savoir, pour toute la semaine visible, quels rendez-vous ont
    // au moins une note — un seul appel groupé plutôt qu'un par bulle.
    const [linkedNoteApptIds, setLinkedNoteApptIds] = useState<Set<string>>(new Set());
    const [quickNotesAppt, setQuickNotesAppt] = useState<Appointment | null>(null);
    const visibleApptIdsKey = useMemo(() => {
        const ids = viewMode === 'day' ? appointments.map((a) => a.id) : Object.values(weekAppointments).flat().map((a) => a.id);
        return Array.from(new Set(ids)).sort().join(',');
    }, [appointments, weekAppointments, viewMode]);
    useEffect(() => {
        const ids = visibleApptIdsKey ? visibleApptIdsKey.split(',') : [];
        notesApi.getLinkedIds('appointment', ids).then((r: any) => setLinkedNoteApptIds(new Set(r.ids || []))).catch(() => {});
    }, [visibleApptIdsKey]);

    useEffect(() => {
        (async () => {
            try {
                const [settings, emps, billing] = await Promise.all([
                    moduleApi.getPrestationPlanningSettings(),
                    moduleApi.getEmployes(),
                    moduleApi.getBillingSettings(),
                ]);
                if (settings?.weekly_hours) setWeeklyHours({ ...defaultWeeklyHours(), ...settings.weekly_hours });
                setEmployees((Array.isArray(emps) ? emps : []).filter((e: any) => !e.deleted_at));
                let parsed: any[] = [];
                try { parsed = JSON.parse(billing?.prestations_catalog || '[]'); } catch { parsed = []; }
                setCatalog((Array.isArray(parsed) ? parsed : []).map((p: any) => ({
                    id: String(p?.id || ''),
                    label: String(p?.label || ''),
                    unit_price_ttc: Number(p?.unit_price_ttc ?? 0),
                    tax_rate: Number(p?.tax_rate ?? 0),
                    show_tva: p?.show_tva !== false,
                    default_duration_minutes: Number(p?.default_duration_minutes ?? 0),
                })).filter((p: PrestationCatalogItem) => p.label.length > 0));
            } catch (e) {
                console.error('Failed to load prestation planning setup', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const loadDay = async (d: string) => {
        try {
            const rows = await moduleApi.getPrestationAppointmentsForDay(d);
            setAppointments(Array.isArray(rows) ? rows : []);
        } catch (e) {
            console.error('Failed to load appointments', e);
            setAppointments([]);
        }
    };

    const weekDates = useMemo(() => {
        const d = new Date(date + 'T00:00:00');
        const day = d.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        const monday = new Date(d);
        monday.setDate(d.getDate() + diffToMonday);
        const dates: string[] = [];
        for (let i = 0; i < 7; i++) {
            const dd = new Date(monday);
            dd.setDate(monday.getDate() + i);
            dates.push(formatDateInput(dd));
        }
        return dates;
    }, [date]);

    const loadWeek = async (dates: string[]) => {
        try {
            const results = await Promise.all(dates.map((d) => moduleApi.getPrestationAppointmentsForDay(d)));
            const map: Record<string, Appointment[]> = {};
            dates.forEach((d, i) => { map[d] = Array.isArray(results[i]) ? results[i] : []; });
            setWeekAppointments(map);
        } catch (e) {
            console.error('Failed to load week appointments', e);
        }
    };

    const reloadCurrentView = async () => {
        if (viewMode === 'day') await loadDay(date);
        else await loadWeek(weekDates);
    };

    useEffect(() => {
        if (viewMode === 'day') loadDay(date);
        else loadWeek(weekDates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, viewMode]);

    // Une fois le jour ciblé chargé, ouvre directement la demande visée par le deep-link — puis nettoie
    // l'URL pour qu'un rafraîchissement de la page n'essaie pas de la rouvrir indéfiniment.
    useEffect(() => {
        if (!pendingFocusId) return;
        const found = appointments.find((a) => a.id === pendingFocusId);
        if (found) {
            setDetailAppointment(found);
            setPendingFocusId(null);
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [appointments, pendingFocusId]);

    const shiftPeriod = (direction: 1 | -1) => {
        const deltaDays = (viewMode === 'week' ? 7 : 1) * direction;
        const d = new Date(date + 'T00:00:00');
        d.setDate(d.getDate() + deltaDays);
        setDate(formatDateInput(d));
    };

    const dayKey = dayKeyForDate(date);
    const dayHours = weeklyHours[dayKey];
    const isDayClosed = dayHours.closed;
    const openingMinutes = timeToMinutes(dayHours.opening);
    const closingMinutes = timeToMinutes(dayHours.closing);

    const weekAxis = useMemo(() => {
        const openDays = weekDates.map((d) => weeklyHours[dayKeyForDate(d)]).filter((h) => !h.closed);
        if (openDays.length === 0) return { opening: timeToMinutes('08:00'), closing: timeToMinutes('20:00') };
        const opening = Math.min(...openDays.map((h) => timeToMinutes(h.opening)));
        const closing = Math.max(...openDays.map((h) => timeToMinutes(h.closing)));
        return { opening, closing: Math.max(closing, opening + 60) };
    }, [weekDates, weeklyHours]);

    const axisOpeningMinutes = viewMode === 'day' ? openingMinutes : weekAxis.opening;
    const axisClosingMinutes = viewMode === 'day' ? closingMinutes : weekAxis.closing;
    const axisTotalMinutes = Math.max(60, axisClosingMinutes - axisOpeningMinutes);
    const axisTotalHeight = (axisTotalMinutes / 60) * PX_PER_HOUR;

    const hourMarks = useMemo(() => {
        const marks: number[] = [];
        for (let m = axisOpeningMinutes; m <= axisClosingMinutes; m += 60) marks.push(m);
        return marks;
    }, [axisOpeningMinutes, axisClosingMinutes]);

    const weekRangeLabel = () => {
        const start = new Date(weekDates[0] + 'T00:00:00');
        const end = new Date(weekDates[6] + 'T00:00:00');
        const startStr = start.toLocaleDateString('fr-FR', { day: 'numeric', month: start.getMonth() === end.getMonth() ? undefined : 'long' });
        const endStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
        return `${startStr} – ${endStr}`;
    };

    const openNewAppointment = (dayDate: string) => {
        setModalState({ open: true, appointment: null, date: dayDate });
    };

    const openEditAppointment = (appt: Appointment) => {
        setDetailAppointment(null);
        setModalState({ open: true, appointment: appt, date: appt.appointment_date });
    };

    const handleDeleteFromDetail = async () => {
        if (!detailAppointment) return;
        await moduleApi.deletePrestationAppointment(detailAppointment.id);
        setDetailAppointment(null);
        await reloadCurrentView();
    };

    const handleSaveAppointment = async (payload: any) => {
        if (modalState.appointment) {
            const res: any = await moduleApi.updatePrestationAppointment(modalState.appointment.id, payload);
            if (res?.was_pending_request) {
                window.dispatchEvent(new Event('prestationBookingRequestProcessed'));
            }
        } else {
            const res: any = await moduleApi.createPrestationAppointment(payload);
            if (Array.isArray(res?.recurrence_skipped_dates) && res.recurrence_skipped_dates.length > 0) {
                alert(`Rendez-vous créé. ${res.recurrence_skipped_dates.length} occurrence(s) n'ont pas pu être créées à cause d'un conflit avec un salarié déjà assigné ce jour-là : ${res.recurrence_skipped_dates.join(', ')}`);
            }
        }
        setModalState({ open: false, appointment: null, date });
        await reloadCurrentView();
    };

    const handleDeleteAppointment = async () => {
        if (!modalState.appointment) return;
        await moduleApi.deletePrestationAppointment(modalState.appointment.id);
        setModalState({ open: false, appointment: null, date });
        await reloadCurrentView();
    };

    if (loading) {
        return (
            <div className="py-20 text-center text-content-muted flex flex-col items-center gap-2">
                <Loader2 size={28} className="animate-spin" />
                <span className="text-sm">Chargement du planning...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <header className="space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-500">
                            <CalendarClock size={22} />
                        </div>
                        <div>
                            <h1 className="text-2xl font-black text-content">Planning</h1>
                            <p className="text-sm text-content-muted mt-1 capitalize">{viewMode === 'day' ? formatDateHuman(date) : weekRangeLabel()}</p>
                            {pendingRequestCount > 0 && (
                                <p className="text-xs text-warning-500 font-bold mt-1">{pendingRequestCount} demande{pendingRequestCount > 1 ? 's' : ''} de réservation à confirmer</p>
                            )}
                        </div>
                    </div>
                    {user?.type === 'client' && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative">
                                <button onClick={toggleBell}
                                    className="relative p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all">
                                    <Bell size={16} />
                                    {pendingRequestCount > 0 && (
                                        <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center shadow-lg shadow-red-500/40 animate-pulse">
                                            {pendingRequestCount > 99 ? '99+' : pendingRequestCount}
                                        </span>
                                    )}
                                </button>
                                {showPendingBell && (
                                    <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-surface border border-border rounded-2xl shadow-2xl z-40">
                                        <div className="px-4 py-3 border-b border-border">
                                            <p className="text-xs font-black uppercase tracking-wider text-content-muted">Demandes en attente</p>
                                        </div>
                                        {loadingPendingList ? (
                                            <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-content-muted" /></div>
                                        ) : pendingRequestsList.length === 0 ? (
                                            <p className="px-4 py-6 text-xs text-content-muted text-center">Aucune demande en attente.</p>
                                        ) : (
                                            pendingRequestsList.map((appt) => (
                                                <button key={appt.id} onClick={() => openPendingFromBell(appt)}
                                                    className="w-full text-left px-4 py-3 border-b border-border last:border-b-0 hover:bg-surface-soft transition-colors">
                                                    <p className="text-sm font-bold text-content truncate">{contactLabel(appt)}</p>
                                                    <p className="text-xs text-content-muted mt-0.5">{formatDateHuman(appt.appointment_date)} à {appt.start_time} — {appt.prestation_label}</p>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                            <button onClick={openUpcomingModal} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                <CalendarClock size={14} />
                                RDV à venir
                            </button>
                            <button onClick={() => navigate('/demandes-recurrentes')} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                <ClipboardList size={14} />
                                Paiement
                            </button>
                            <button onClick={() => setShowSendDocumentModal(true)} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                <Link2 size={14} />
                                Envoyer une démarche client
                            </button>
                            <button onClick={() => { setShowSendLinkModal(true); setSendLinkMessage(null); }} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                                <Link2 size={14} />
                                Envoyer un lien de rendez-vous
                            </button>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    <div className="flex items-center gap-1 bg-surface-soft border border-border rounded-xl p-1">
                        <button onClick={() => setViewMode('week')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'week' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>Semaine</button>
                        <button onClick={() => setViewMode('day')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'day' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>Jour</button>
                    </div>
                    <button onClick={() => shiftPeriod(-1)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronLeft size={16} /></button>
                    <input type="date" value={viewMode === 'week' ? weekDates[0] : date} onChange={(e) => setDate(e.target.value)}
                        className="bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                    <button onClick={() => shiftPeriod(1)} className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all"><ChevronRight size={16} /></button>
                    <button onClick={() => setDate(formatDateInput(new Date()))} className="px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-xs font-bold hover:bg-surface-soft transition-all">Aujourd'hui</button>
                    <button onClick={() => openNewAppointment(date)} disabled={viewMode === 'day' && isDayClosed}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold shadow-lg shadow-brand-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                        <Plus size={16} /> Nouveau
                    </button>
                </div>
            </header>

            {employees.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl bg-surface/40">
                    <UsersIcon size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Configurez d'abord vos employés dans "Postes &amp; Employés" pour planifier des rendez-vous.</p>
                </div>
            ) : viewMode === 'day' && isDayClosed ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl bg-surface/40">
                    <Clock size={32} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Fermé le {DAY_LABELS[dayKey].toLowerCase()} — modifiez les horaires dans Paramètres si besoin.</p>
                </div>
            ) : (
                <div className="rounded-2xl border border-border bg-surface overflow-hidden">
                    <div className="overflow-x-auto">
                        <div className="flex min-w-max">
                            <div className="w-16 shrink-0 border-r border-border">
                                <div className="h-12 border-b border-border" />
                                <div className="relative" style={{ height: axisTotalHeight }}>
                                    {hourMarks.map((m) => (
                                        <div key={m} className="absolute left-0 right-0 text-[10px] text-content-muted px-1 -translate-y-1/2"
                                            style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }}>
                                            {minutesToTime(m)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {(viewMode === 'day' ? [date] : weekDates).map((d) => {
                                const dHours = weeklyHours[dayKeyForDate(d)];
                                const dAppointments = viewMode === 'day' ? appointments : (weekAppointments[d] || []);
                                const { packed, laneCount } = packAppointments(dAppointments);
                                return (
                                    <div key={d} className={viewMode === 'day' ? 'flex-1 min-w-[320px] border-r border-border last:border-r-0' : 'flex-1 min-w-[150px] border-r border-border last:border-r-0'}>
                                        <div className="h-12 border-b border-border flex items-center justify-center px-2">
                                            <span className="text-xs font-bold text-content truncate">{viewMode === 'day' ? formatDateHuman(d) : dayShortLabel(d)}</span>
                                        </div>
                                        <div
                                            className="relative"
                                            style={{ height: axisTotalHeight }}
                                            onClick={() => !dHours.closed && openNewAppointment(d)}
                                        >
                                            {dHours.closed ? (
                                                <div className="absolute inset-0 flex items-center justify-center text-[11px] text-content-muted italic">Fermé</div>
                                            ) : (
                                                <>
                                                    {hourMarks.map((m) => (
                                                        <div key={m} className="absolute left-0 right-0 border-t border-border/50"
                                                            style={{ top: ((m - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight }} />
                                                    ))}
                                                    {packed.map(({ appt, lane }) => {
                                                        const startMin = timeToMinutes(appt.start_time);
                                                        const endTime = minutesToTime(startMin + Math.round(appt.computed_duration_minutes));
                                                        const top = Math.max(0, ((startMin - axisOpeningMinutes) / axisTotalMinutes) * axisTotalHeight);
                                                        const minHeight = Math.max(28, (appt.computed_duration_minutes / axisTotalMinutes) * axisTotalHeight);
                                                        const width = 100 / laneCount;
                                                        const left = lane * width;
                                                        const isPendingRequest = appt.status === 'demande';
                                                        return (
                                                            <button
                                                                key={appt.id}
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setDetailAppointment(appt); }}
                                                                className={`absolute z-10 hover:z-20 rounded-lg transition-all px-1.5 py-1 text-left shadow-sm ${
                                                                    isPendingRequest
                                                                        ? 'bg-warning-600 border-2 border-dashed border-warning-400 hover:bg-warning-500'
                                                                        : 'bg-brand-600 border border-brand-700 hover:bg-brand-500'
                                                                }`}
                                                                style={{ top, minHeight, left: `${left}%`, width: `calc(${width}% - 4px)` }}
                                                            >
                                                                {linkedNoteApptIds.has(appt.id) && (
                                                                    <span title="Voir les notes attachées"
                                                                        onClick={(e) => { e.stopPropagation(); setQuickNotesAppt(appt); }}
                                                                        className="absolute top-1 right-1 text-white/90 hover:text-white cursor-pointer">
                                                                        <NotebookText size={10} />
                                                                    </span>
                                                                )}
                                                                {isPendingRequest && (
                                                                    <p className="text-[8px] font-black uppercase text-white leading-snug">Demande à confirmer</p>
                                                                )}
                                                                <p className="text-[10px] font-bold text-white leading-snug break-words">{contactLabel(appt)}</p>
                                                                <p className="text-[9px] text-white/80 leading-snug">{appt.start_time} - {endTime}</p>
                                                                <p className="text-[9px] text-white/80 leading-snug break-words">{appt.prestation_label}</p>
                                                                <p className="text-[9px] text-white/90 font-semibold leading-snug break-words">{isPendingRequest ? 'Aucun salarié assigné' : employeeNamesLabel(appt, employees)}</p>
                                                            </button>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {showSendDocumentModal && (
                <SendDocumentByEmailModal onClose={() => setShowSendDocumentModal(false)} onSent={() => {}} />
            )}

            {showSendLinkModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                            <h3 className="text-lg font-bold text-content flex items-center gap-2"><Link2 size={18} className="text-brand-500" /> Envoyer un lien de rendez-vous</h3>
                            <button onClick={() => setShowSendLinkModal(false)} className="text-content-muted hover:text-content"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-content-muted">Le client recevra un email avec un lien vers votre page de rendez-vous en ligne, où apparaissent tous les types de rendez-vous que vous avez configurés (Paramètres &gt; Démarches client, case "Faire apparaître dans le widget"). Il choisit le type, remplit ses informations — sa fiche client existante est automatiquement mise à jour si l'adresse email correspond déjà à un contact.</p>
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Email du client</label>
                                <input type="email" autoFocus value={sendLinkEmail} onChange={(e) => setSendLinkEmail(e.target.value)}
                                    placeholder="client@exemple.com"
                                    className="w-full bg-surface-soft border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                            </div>
                            {sendLinkMessage && (
                                <p className={`text-xs font-semibold ${sendLinkMessage.type === 'success' ? 'text-emerald-500' : 'text-red-400'}`}>{sendLinkMessage.text}</p>
                            )}
                            <button onClick={handleSendBookingLink} disabled={sendingLink || !sendLinkEmail.trim()}
                                className="w-full px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 transition-all">
                                {sendingLink ? 'Envoi...' : 'Envoyer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showUpcomingModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[80vh] shadow-2xl overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
                            <h3 className="text-lg font-bold text-content flex items-center gap-2"><CalendarClock size={18} className="text-brand-500" /> Rendez-vous à venir</h3>
                            <button onClick={() => setShowUpcomingModal(false)} className="text-content-muted hover:text-content"><X size={20} /></button>
                        </div>
                        <div className="overflow-y-auto">
                            {loadingUpcoming ? (
                                <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin text-content-muted" /></div>
                            ) : upcomingAppointments.length === 0 ? (
                                <p className="px-6 py-10 text-sm text-content-muted text-center">Aucun rendez-vous confirmé à venir.</p>
                            ) : (
                                upcomingAppointments.map((appt) => (
                                    <button key={appt.id} onClick={() => openUpcomingAppointment(appt)}
                                        className="w-full text-left px-6 py-4 border-b border-border last:border-b-0 hover:bg-surface-soft transition-colors">
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-sm font-bold text-content truncate">{contactLabel(appt)}</p>
                                            <p className="text-xs text-content-muted shrink-0 capitalize">{formatDateHuman(appt.appointment_date)}</p>
                                        </div>
                                        <p className="text-xs text-content-muted mt-0.5">{appt.start_time} — {appt.prestation_label}</p>
                                        <p className="text-xs text-content-muted mt-0.5">{employeeNamesLabel(appt, employees) || 'Aucun salarié assigné'}</p>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            <AnimatePresence>
                {modalState.open && (
                    <AppointmentModal
                        initial={modalState.appointment}
                        catalog={catalog}
                        employees={employees}
                        date={modalState.date}
                        weeklyHours={weeklyHours}
                        onClose={() => setModalState({ open: false, appointment: null, date })}
                        onSave={handleSaveAppointment}
                        onDelete={modalState.appointment ? handleDeleteAppointment : undefined}
                    />
                )}
                {detailAppointment && (
                    <AppointmentDetailModal
                        appt={detailAppointment}
                        employees={employees}
                        catalog={catalog}
                        onClose={() => setDetailAppointment(null)}
                        onEdit={() => openEditAppointment(detailAppointment)}
                        onDelete={handleDeleteFromDetail}
                        onRefresh={reloadCurrentView}
                    />
                )}
                {quickNotesAppt && (
                    <LinkedNotesQuickModal kind="appointment" id={quickNotesAppt.id} label={contactLabel(quickNotesAppt)} onClose={() => setQuickNotesAppt(null)} />
                )}
            </AnimatePresence>
        </div>
    );
};
